// src/components/admin/AdminUsers.tsx - VERSIÓN COMPLETA FUNCIONAL
import React, { useState, useEffect } from 'react';
import apiElCristo from '../../service/api_elcristo';
import './AdminUsers.css';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at?: string;
}

interface AdminUsersProps {
  currentUser: any;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'editor',
    password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getUsers();
      console.log('📥 Users data:', data);
      
      const usersArray = Array.isArray(data) ? data : Object.values(data);
      setUsers(usersArray as User[]);
    } catch (error) {
      console.error('❌ Error loading users:', error);
      setMessage({ type: 'error', text: 'Error al cargar usuarios' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        password: ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        role: 'editor',
        password: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      role: 'editor',
      password: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      if (editingUser) {
        console.log('📤 Updating user:', editingUser.id, formData);
        await apiElCristo.updateUser(editingUser.id, formData);
        setMessage({ type: 'success', text: '✅ Usuario actualizado' });
      } else {
        if (!formData.password) {
          setMessage({ type: 'error', text: '❌ La contraseña es requerida' });
          setSaving(false);
          setTimeout(() => setMessage(null), 3000);
          return;
        }
        console.log('📤 Creating user:', formData);
        await apiElCristo.createUser(formData);
        setMessage({ type: 'success', text: '✅ Usuario creado' });
      }

      handleCloseModal();
      await loadUsers();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('❌ Error saving user:', error);
      setMessage({ type: 'error', text: `❌ Error: ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (user: User) => {
    if (user.id === currentUser?.id) {
      setMessage({ type: 'error', text: '❌ No puedes eliminar tu propio usuario' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setSaving(true);

    try {
      console.log('📤 Deleting user:', userToDelete.id);
      await apiElCristo.deleteUser(userToDelete.id);
      
      setMessage({ type: 'success', text: '✅ Usuario eliminado' });
      handleCloseDeleteModal();
      await loadUsers();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('❌ Error deleting user:', error);
      setMessage({ type: 'error', text: `❌ Error: ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin': return 'badge-admin';
      case 'editor': return 'badge-editor';
      case 'viewer': return 'badge-viewer';
      default: return 'badge-default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return 'fa-crown';
      case 'editor': return 'fa-pen';
      case 'viewer': return 'fa-eye';
      default: return 'fa-user';
    }
  };

  const getRoleName = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'editor': return 'Editor';
      case 'viewer': return 'Visor';
      default: return role;
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="admin-users-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-user-shield"></i> Gestión de Usuarios</h1>
          <p>Administrar accesos al panel</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-user-plus"></i>
          Nuevo Usuario
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="filters-row">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-select">
              <i className="fas fa-filter"></i>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="all">Todos los roles</option>
                <option value="admin">Administradores</option>
                <option value="editor">Editores</option>
                <option value="viewer">Visores</option>
              </select>
            </div>

            <div className="stats-badge">
              <i className="fas fa-users"></i>
              <span>{filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <p>No se encontraron usuarios</p>
              {searchTerm && (
                <button type="button" className="btn btn-secondary" onClick={() => setSearchTerm('')}>
                  Limpiar búsqueda
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body no-padding">
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Creado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{user.name}</span>
                            {user.id === currentUser?.id && (
                              <span className="you-badge">Tú</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="email-cell">
                          <i className="fas fa-envelope"></i>
                          {user.email}
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                          <i className={`fas ${getRoleIcon(user.role)}`}></i>
                          {getRoleName(user.role)}
                        </span>
                      </td>
                      <td>
                        <div className="date-cell">
                          <i className="fas fa-calendar"></i>
                          {user.created_at 
                            ? new Date(user.created_at).toLocaleDateString('es-CL')
                            : 'N/A'
                          }
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="btn-icon btn-edit"
                            onClick={() => handleOpenModal(user)}
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          {user.id !== currentUser?.id && (
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleOpenDeleteModal(user)}
                              title="Eliminar"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CREAR/EDITAR */}
      {showModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '600px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-user"></i>
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="team-modal-body">
                <div className="form-group">
                  <label><i className="fas fa-user"></i> Nombre Completo *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Juan Pérez"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-envelope"></i> Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="usuario@colegioelcristo.cl"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-user-tag"></i> Rol *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="form-control"
                  >
                    <option value="admin">Administrador - Acceso total</option>
                    <option value="editor">Editor - Editar contenido</option>
                    <option value="viewer">Visor - Solo lectura</option>
                  </select>
                  <small className="hint">
                    <i className="fas fa-info-circle"></i>
                    {formData.role === 'admin' && ' Puede crear/editar usuarios y contenido'}
                    {formData.role === 'editor' && ' Puede editar contenido pero no usuarios'}
                    {formData.role === 'viewer' && ' Solo puede ver el contenido sin editar'}
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-lock"></i> Contraseña 
                    {editingUser && <span style={{ color: '#999', fontWeight: 'normal' }}> (dejar vacío para no cambiar)</span>}
                    {!editingUser && ' *'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    placeholder="••••••••"
                    minLength={8}
                    className="form-control"
                  />
                  <small className="hint">
                    <i className="fas fa-shield-alt"></i> Mínimo 8 caracteres
                  </small>
                </div>

                <div className="role-preview">
                  <h4><i className="fas fa-info-circle"></i> Permisos del rol:</h4>
                  <div className="permissions-list">
                    {formData.role === 'admin' && (
                      <>
                        <div className="permission-item">
                          <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                          <span>Gestionar usuarios (crear, editar, eliminar)</span>
                        </div>
                        <div className="permission-item">
                          <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                          <span>Editar todo el contenido del sitio</span>
                        </div>
                        <div className="permission-item">
                          <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                          <span>Acceso completo al panel de administración</span>
                        </div>
                      </>
                    )}
                    {formData.role === 'editor' && (
                      <>
                        <div className="permission-item">
                          <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                          <span>Editar contenido del sitio</span>
                        </div>
                        <div className="permission-item">
                          <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                          <span>Subir imágenes y archivos</span>
                        </div>
                        <div className="permission-item">
                          <i className="fas fa-times-circle" style={{ color: '#f44336' }}></i>
                          <span>No puede gestionar usuarios</span>
                        </div>
                      </>
                    )}
                    {formData.role === 'viewer' && (
                      <>
                        <div className="permission-item">
                          <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                          <span>Ver contenido del sitio</span>
                        </div>
                        <div className="permission-item">
                          <i className="fas fa-times-circle" style={{ color: '#f44336' }}></i>
                          <span>No puede editar contenido</span>
                        </div>
                        <div className="permission-item">
                          <i className="fas fa-times-circle" style={{ color: '#f44336' }}></i>
                          <span>No puede gestionar usuarios</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="team-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <><div className="spinner-small"></div> Guardando...</>
                  ) : (
                    <><i className="fas fa-save"></i> {editingUser ? 'Actualizar' : 'Crear Usuario'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DELETE */}
      {showDeleteModal && userToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseDeleteModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Eliminar usuario <strong>"{userToDelete.name}"</strong>?
              </p>
              <div style={{ padding: '15px', background: '#fff3cd', borderRadius: '8px', marginBottom: '15px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
                  <i className="fas fa-exclamation-circle"></i> 
                  {' '}Este usuario perderá acceso al panel de administración.
                </p>
              </div>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Eliminando...</>
                ) : (
                  <><i className="fas fa-trash"></i> Eliminar Usuario</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;